/**
 * Map Utility Functions
 * Color scales, classification, and style functions for choropleth maps
 */

// === Color Scales (DHIS2-inspired) ===

export const COLOR_SCALES = {
  enrollment: ['#E3F2FD', '#90CAF9', '#42A5F5', '#1E88E5', '#0D47A1'],
  schools: ['#E8F5E9', '#A5D6A7', '#66BB6A', '#388E3C', '#1B5E20'],
  gender_parity: ['#C62828', '#EF5350', '#FFEE58', '#66BB6A', '#2E7D32'],
  teachers: ['#FFF3E0', '#FFB74D', '#FF9800', '#F57C00', '#E65100'],
  performance: ['#FCE4EC', '#F48FB1', '#EC407A', '#C2185B', '#880E4F'],
};

export type IndicatorType = 'enrollment' | 'schools' | 'gender_parity' | 'teachers' | 'performance';

// === Tile Layer Configs ===

export const TILE_LAYERS = {
  standard: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    name: 'Standard',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    name: 'Satellite',
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors',
    name: 'Terrain',
  },
};

// === Zanzibar Map Constants ===

export const ZANZIBAR_CENTER: [number, number] = [-6.15, 39.20];
export const ZANZIBAR_BOUNDS: [[number, number], [number, number]] = [
  [-7.0, 38.5],  // SW corner
  [-4.8, 40.0],  // NE corner
];
export const DEFAULT_ZOOM = 9;
export const REGION_ZOOM = 10;
export const DISTRICT_ZOOM = 11;
export const WARD_ZOOM = 13;

// === Classification ===

/**
 * Classify values into N equal-interval classes
 */
export function classifyValues(values: number[], numClasses: number = 5): number[] {
  if (values.length === 0) return [];

  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  if (min === max) return [max];

  const interval = (max - min) / numClasses;
  const breaks: number[] = [];

  for (let i = 1; i <= numClasses; i++) {
    breaks.push(Math.round(min + interval * i));
  }

  return breaks;
}

/**
 * Classify using natural breaks (Jenks-like simplified)
 */
export function classifyNaturalBreaks(values: number[], numClasses: number = 5): number[] {
  if (values.length <= numClasses) return values.sort((a, b) => a - b);

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const step = Math.floor(n / numClasses);

  const breaks: number[] = [];
  for (let i = 1; i < numClasses; i++) {
    breaks.push(sorted[i * step]);
  }
  breaks.push(sorted[n - 1]);

  return breaks;
}

/**
 * Get color for a value based on classification breaks
 */
export function getColor(value: number, breaks: number[], colors: string[]): string {
  for (let i = 0; i < breaks.length; i++) {
    if (value <= breaks[i]) {
      return colors[i] || colors[colors.length - 1];
    }
  }
  return colors[colors.length - 1];
}

/**
 * Get the full color palette for an indicator
 */
export function getColorScale(indicator: IndicatorType): string[] {
  return COLOR_SCALES[indicator] || COLOR_SCALES.enrollment;
}

// === Feature Style Functions ===

export interface FeatureStyleOptions {
  value?: number;
  breaks: number[];
  colors: string[];
  isSelected?: boolean;
  isHovered?: boolean;
}

export function getFeatureStyle(options: FeatureStyleOptions) {
  const { value, breaks, colors, isSelected, isHovered } = options;

  const fillColor = value !== undefined ? getColor(value, breaks, colors) : '#CCCCCC';

  return {
    fillColor,
    weight: isSelected ? 3 : isHovered ? 2 : 1,
    opacity: 1,
    color: isSelected ? '#1976D2' : isHovered ? '#333' : '#666',
    dashArray: isSelected ? '' : '3',
    fillOpacity: isSelected ? 0.8 : isHovered ? 0.75 : 0.65,
  };
}

export function getHighlightStyle() {
  return {
    weight: 3,
    color: '#333',
    dashArray: '',
    fillOpacity: 0.8,
  };
}

export function getDefaultStyle(fillColor: string) {
  return {
    fillColor,
    weight: 1,
    opacity: 1,
    color: '#666',
    dashArray: '3',
    fillOpacity: 0.65,
  };
}

// === School Marker Styles ===

export function getSchoolMarkerColor(schoolType: string): string {
  switch (schoolType?.toLowerCase()) {
    case 'government':
    case 'public':
      return '#1565C0';
    case 'private':
      return '#E65100';
    case 'community':
      return '#2E7D32';
    default:
      return '#757575';
  }
}

export function getSchoolMarkerRadius(enrollment?: number): number {
  if (!enrollment) return 4;
  if (enrollment > 1000) return 8;
  if (enrollment > 500) return 6;
  if (enrollment > 100) return 5;
  return 4;
}

// === Data Joining ===

export interface GeoFeatureData {
  [key: string]: number | string | undefined;
  total_students?: number;
  total_schools?: number;
  male_students?: number;
  female_students?: number;
  gender_parity?: number;
}

/**
 * Join GeoJSON features with enrollment data by matching codes/names
 */
export function joinGeoData(
  features: GeoJSON.Feature[],
  enrollmentData: any[],
  level: 'region' | 'district' | 'ward'
): Map<string, GeoFeatureData> {
  const dataMap = new Map<string, GeoFeatureData>();

  for (const feature of features) {
    const props = feature.properties || {};
    let matchKey = '';
    let matchData: any = null;

    if (level === 'region') {
      // Match by reg_code (GeoJSON) to region_code (API)
      const regCode = String(props.reg_code || '');
      matchData = enrollmentData.find(
        (d) => String(d.region_code) === regCode
      );
      matchKey = regCode;
    } else if (level === 'district') {
      // Match by dist_name (GeoJSON) to district_name (API) - case insensitive
      const distName = String(props.dist_name || '').toLowerCase().trim();
      matchData = enrollmentData.find(
        (d) => String(d.district_name || '').toLowerCase().trim() === distName
      );
      matchKey = props.dist_name || props.dist_code || '';
    } else if (level === 'ward') {
      // Ward matching - by ward name if available
      const wardName = String(props.ward_name || '').toLowerCase().trim();
      matchData = enrollmentData.find(
        (d) => String(d.ward_name || '').toLowerCase().trim() === wardName
      );
      matchKey = props.ward_name || props.ward_code || '';
    }

    if (matchKey) {
      const totalStudents = matchData?.total_students || 0;
      const maleStudents = matchData?.male_students || 0;
      const femaleStudents = matchData?.female_students || 0;

      dataMap.set(matchKey, {
        total_students: totalStudents,
        total_schools: matchData?.total_schools || 0,
        male_students: maleStudents,
        female_students: femaleStudents,
        gender_parity: maleStudents > 0
          ? Math.round((femaleStudents / maleStudents) * 100) / 100
          : 0,
        ...matchData,
      });
    }
  }

  return dataMap;
}

/**
 * Get the indicator value for a feature
 */
export function getFeatureValue(
  feature: GeoJSON.Feature,
  dataMap: Map<string, GeoFeatureData>,
  indicator: IndicatorType,
  level: 'region' | 'district' | 'ward'
): number | undefined {
  const props = feature.properties || {};
  let matchKey = '';

  if (level === 'region') {
    matchKey = String(props.reg_code || '');
  } else if (level === 'district') {
    matchKey = props.dist_name || props.dist_code || '';
  } else if (level === 'ward') {
    matchKey = props.ward_name || props.ward_code || '';
  }

  const data = dataMap.get(matchKey);
  if (!data) return undefined;

  switch (indicator) {
    case 'enrollment':
      return data.total_students;
    case 'schools':
      return data.total_schools;
    case 'gender_parity':
      return data.gender_parity;
    default:
      return data.total_students;
  }
}

/**
 * Format a number for display (with commas)
 */
export function formatNumber(value: number | undefined): string {
  if (value === undefined || value === null) return 'N/A';
  return value.toLocaleString();
}

/**
 * Get display name from GeoJSON feature properties
 */
export function getFeatureName(
  feature: GeoJSON.Feature,
  level: 'region' | 'district' | 'ward'
): string {
  const props = feature.properties || {};

  if (level === 'region') return props.reg_name || props.region_name || 'Unknown Region';
  if (level === 'district') return props.dist_name || props.district_name || 'Unknown District';
  if (level === 'ward') return props.ward_name || 'Unknown Ward';

  return 'Unknown';
}

/**
 * Get the indicator label for display
 */
export function getIndicatorLabel(indicator: IndicatorType): string {
  switch (indicator) {
    case 'enrollment': return 'Student Enrollment';
    case 'schools': return 'Number of Schools';
    case 'gender_parity': return 'Gender Parity Index';
    case 'teachers': return 'Teachers';
    case 'performance': return 'Performance';
    default: return 'Enrollment';
  }
}
